import type { FetchResult } from '@apollo/client';
import isNil from 'lodash/isNil';
import noop from 'lodash/noop';
import type { PickDeep } from 'type-fest';

import type { NfsErrorFragment as AdminNfsError, UserErrorUnionInterface as AdminUserErrorUnionInterface } from '../schema/admin';
import {
  isInstanceOf, NfsErrorTypename, UserFacingAuthzErrorTypename, ValidationErrorTypename,
} from '../schema/admin';
import type { NfsErrorFragment as UserNfsError, UserErrorUnionInterface as UserUserErrorUnionInterface } from '../schema/user';

import { isEmpty } from './checks';
import { isLiteral, isNotNil, isObjectWithKeys } from './guards';
import type { Nullable, PartialDeepObject } from './object';

export type NfsErrorFragment = AdminNfsError | UserNfsError;

interface TypeLike { __typename?: string }

export type NonError<ErrorUnionType> = Exclude<
  ErrorUnionType, { __typename?: typeof NfsErrorTypename }>;

export type Error<ErrorUnionType> = Extract<
  ErrorUnionType, { __typename?: typeof NfsErrorTypename }>;

export const getData = <ErrorUnionType extends TypeLike>(
  union?: Nullable<ErrorUnionType>,
): Nullable<NonError<ErrorUnionType>> => {
  if (isNil(union)) {
    return null;
  }

  if (isInstanceOf(union, NfsErrorTypename)) {
    return null;
  }

  return union as NonError<ErrorUnionType>;
};

export const getError = <ErrorUnionType extends TypeLike>(
  union?: Nullable<ErrorUnionType>,
): Nullable<Error<ErrorUnionType>> => {
  if (isNil(union)) {
    return null;
  }

  if (isInstanceOf(union, NfsErrorTypename)) {
    return union;
  }

  return null;
};

export type ErrorTuple<ErrorUnionType> = [
  data: Nullable<NonError<ErrorUnionType>>,
  error: Nullable<Error<ErrorUnionType>>,
];

export const splitError = <ErrorUnionType extends TypeLike>(
  union?: Nullable<ErrorUnionType>,
): ErrorTuple<ErrorUnionType> => [getData(union), getError(union)];

export const throwUnhandled = (
  error: Nullable<PartialDeepObject<PickDeep<AdminNfsError | UserNfsError, 'error.msg'>>>,
): void => {
  if (!isNil(error)) {
    throw new Error(error?.error?.msg);
  }
};

const isNfsError = (arg: unknown): arg is NfsErrorFragment => isObjectWithKeys(arg, ['__typename', 'error'])
  && isInstanceOf(arg, NfsErrorTypename);

export const getNfsError = (arg: unknown): NfsErrorFragment[] => {
  if (isNil(arg)) {
    return [];
  }

  if (isLiteral(arg)) {
    if (isNfsError(arg)) {
      return [arg];
    }

    return Object.values(arg).flatMap(getNfsError);
  }

  return [];
};

const isSpecificNfsError = (arg: unknown, typename: Nullable<NfsErrorFragment['error']['__typename']>): boolean => isNfsError(arg) && arg.error.__typename === typename;

export const isUnauthorizedQuery = (arg: unknown): boolean => isSpecificNfsError(arg, UserFacingAuthzErrorTypename);

export const isSuccessfulResponse = (
  fetchResult: FetchResult,
): boolean => isNil(fetchResult.errors) && isEmpty(getNfsError(fetchResult.data));

type ResponseFn<T extends FetchResult> = (fetchResult: T) => unknown;
type ResponseHandler<T extends FetchResult> = (fetchResult: T) => T;

type OnResponseArg<T extends FetchResult> = Readonly<{
  onSuccess?: ResponseFn<T>;
  onError?: ResponseFn<T>;
}>;

export const onResponse = <T extends FetchResult>({
  onSuccess, onError,
}: OnResponseArg<T>): ResponseHandler<T> => (fetchResult) => {
  if (isSuccessfulResponse(fetchResult)) {
    onSuccess?.(fetchResult);
    return fetchResult;
  }

  onError?.(fetchResult);
  return fetchResult;
};

export const onSuccess = <T extends FetchResult>(
  fn: ResponseFn<T>,
): ResponseHandler<T> => onResponse<T>({ onSuccess: fn, onError: noop });

type AllErrorTypenames = NonNullable<AdminUserErrorUnionInterface['__typename'] | UserUserErrorUnionInterface['__typename']>;

type BaseErrorShape = Readonly<{ __typename?: typeof NfsErrorTypename; error?: { __typename?: AllErrorTypenames } }>;

export const ignoreError = <T extends BaseErrorShape>(
  error: Nullable<T>,
  ignored: AllErrorTypenames[],
): Nullable<T> => {
  if (isNil(error)) {
    return null;
  }

  if (ignored.some((errorTypename) => !isNil(error.error) && isInstanceOf(error.error, errorTypename))) {
    return null;
  }

  return error;
};

export const validationErrors = <Path>(
  error: Nullable<NfsErrorFragment>,
  apiFieldToPath: Readonly<Partial<Record<string, Path>>>,
): Readonly<{ path: Path; message: string }>[] => {
  if (isNil(error) || !isInstanceOf(error.error, ValidationErrorTypename)) {
    return [];
  }

  const { fields, msg } = error.error;

  return fields
    .map((field) => apiFieldToPath[field])
    .filter(isNotNil)
    .map((path) => ({ path, message: msg }));
};
